export type Language = 'en' | 'es' | 'hi';

export const LANGUAGES: { value: Language; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'hi', label: 'हिन्दी' },
];

const en = {
  'nav.features': 'Features',
  'nav.platforms': 'Formats',
  'nav.faq': 'FAQ',
  'nav.about': 'About',
  'nav.dashboard': 'Dashboard',
  'nav.signIn': 'Sign in',
  'nav.getStarted': 'Get started',
  'nav.signOut': 'Sign out',
  'tab.overview': 'Home',
  'tab.history': 'History',
  'tab.files': 'Files',
  'tab.settings': 'Settings',
  'tab.admin': 'Admin',
  'hero.badge': 'Fast · Free · No watermarks',
  'hero.title': 'Download Instagram Reels, Photos & Video Instantly',
  'hero.subtitle': 'Paste a public Instagram link and download Reels, posts and video — plus audio and snapshots — in seconds.',
  'hero.ctaPrimary': 'Start Downloading',
  'hero.ctaSecondary': 'View Features',
  'input.placeholder': 'Paste a public Instagram link — Reel, post, IGTV or video…',
  'input.paste': 'Paste',
  'input.process': 'Process media',
  'input.processing': 'Processing…',
  'input.dropHint': 'Drop your link anywhere in the box',
  'input.output.video': 'Video · MP4',
  'input.output.audio': 'Audio · MP3',
  'input.output.snapshots': 'Snapshots',
  'input.quality': 'Quality',
  'input.interval': 'Interval',
  'input.format': 'Format',
  'input.custom': 'Custom',
  'input.seconds': 'sec',
  'status.queued': 'Queued',
  'status.processing': 'Processing',
  'status.completed': 'Completed',
  'status.failed': 'Failed',
  'status.expired': 'Expired',
  'dash.overview': 'Overview',
  'dash.queue': 'Processing queue',
  'dash.recent': 'Recent downloads',
  'dash.history': 'Download history',
  'dash.files': 'Saved files',
  'dash.settings': 'Settings',
  'dash.admin': 'Admin analytics',
  'dash.emptyQueue': 'Nothing processing right now. Paste a URL above to get started.',
  'dash.emptyHistory': 'Your downloads will appear here.',
  'stats.today': 'Downloads today',
  'stats.total': 'Total downloads',
  'stats.storage': 'Storage used',
  'stats.topPlatform': 'Most used platform',
  'common.download': 'Download',
  'common.copy': 'Copy link',
  'common.copied': 'Copied!',
  'common.share': 'Share',
  'common.delete': 'Delete',
  'common.loading': 'Loading…',
  'common.viewAll': 'View all',
  'common.language': 'Language',
  'common.notifications': 'Notifications',
  'common.expiresIn': 'Expires in',
  'footer.disclaimer':
    'Only publicly accessible content is processed. You are responsible for complying with copyright laws and each platform’s Terms of Service.',
  'processing.title': 'Preparing your media…',
  'processing.subtitle': 'Hang tight — cloud functions are fetching and converting your file.',
  'processing.eta': 'Estimated wait',
} as const;

export type TranslationKey = keyof typeof en;

const es: Record<TranslationKey, string> = {
  'nav.features': 'Funciones',
  'nav.platforms': 'Formatos',
  'nav.faq': 'FAQ',
  'nav.about': 'Acerca de',
  'nav.dashboard': 'Panel',
  'nav.signIn': 'Iniciar sesión',
  'nav.getStarted': 'Comenzar',
  'nav.signOut': 'Cerrar sesión',
  'tab.overview': 'Inicio',
  'tab.history': 'Historial',
  'tab.files': 'Archivos',
  'tab.settings': 'Ajustes',
  'tab.admin': 'Admin',
  'hero.badge': 'Rápido · Gratis · Sin marcas de agua',
  'hero.title': 'Descarga Reels, Fotos y Videos de Instagram al Instante',
  'hero.subtitle': 'Pega un enlace público de Instagram y descarga Reels, publicaciones y videos —además de audio y capturas— en segundos.',
  'hero.ctaPrimary': 'Empezar a descargar',
  'hero.ctaSecondary': 'Ver funciones',
  'input.placeholder': 'Pega un enlace público de Instagram — Reel, publicación, IGTV o video…',
  'input.paste': 'Pegar',
  'input.process': 'Procesar',
  'input.processing': 'Procesando…',
  'input.dropHint': 'Suelta tu enlace en el cuadro',
  'input.output.video': 'Video · MP4',
  'input.output.audio': 'Audio · MP3',
  'input.output.snapshots': 'Capturas',
  'input.quality': 'Calidad',
  'input.interval': 'Intervalo',
  'input.format': 'Formato',
  'input.custom': 'Personalizado',
  'input.seconds': 'seg',
  'status.queued': 'En cola',
  'status.processing': 'Procesando',
  'status.completed': 'Completado',
  'status.failed': 'Fallido',
  'status.expired': 'Expirado',
  'dash.overview': 'Resumen',
  'dash.queue': 'Cola de procesamiento',
  'dash.recent': 'Descargas recientes',
  'dash.history': 'Historial de descargas',
  'dash.files': 'Archivos guardados',
  'dash.settings': 'Ajustes',
  'dash.admin': 'Analíticas de admin',
  'dash.emptyQueue': 'Nada en proceso. Pega una URL arriba para comenzar.',
  'dash.emptyHistory': 'Tus descargas aparecerán aquí.',
  'stats.today': 'Descargas hoy',
  'stats.total': 'Descargas totales',
  'stats.storage': 'Almacenamiento usado',
  'stats.topPlatform': 'Plataforma más usada',
  'common.download': 'Descargar',
  'common.copy': 'Copiar enlace',
  'common.copied': '¡Copiado!',
  'common.share': 'Compartir',
  'common.delete': 'Eliminar',
  'common.loading': 'Cargando…',
  'common.viewAll': 'Ver todo',
  'common.language': 'Idioma',
  'common.notifications': 'Notificaciones',
  'common.expiresIn': 'Expira en',
  'footer.disclaimer':
    'Solo se procesa contenido público. Eres responsable de cumplir las leyes de copyright y los Términos de Servicio de cada plataforma.',
  'processing.title': 'Preparando tus medios…',
  'processing.subtitle': 'Un momento — las funciones en la nube están convirtiendo tu archivo.',
  'processing.eta': 'Espera estimada',
};

const hi: Record<TranslationKey, string> = {
  'nav.features': 'विशेषताएँ',
  'nav.platforms': 'फ़ॉर्मैट',
  'nav.faq': 'FAQ',
  'nav.about': 'परिचय',
  'nav.dashboard': 'डैशबोर्ड',
  'nav.signIn': 'साइन इन',
  'nav.getStarted': 'शुरू करें',
  'nav.signOut': 'साइन आउट',
  'tab.overview': 'होम',
  'tab.history': 'इतिहास',
  'tab.files': 'फ़ाइलें',
  'tab.settings': 'सेटिंग्स',
  'tab.admin': 'एडमिन',
  'hero.badge': 'तेज़ · मुफ़्त · बिना वॉटरमार्क',
  'hero.title': 'Instagram Reels, फ़ोटो और वीडियो तुरंत डाउनलोड करें',
  'hero.subtitle': 'सार्वजनिक Instagram लिंक पेस्ट करें और सेकंडों में Reels, पोस्ट और वीडियो — साथ ही ऑडियो और स्नैपशॉट — पाएं।',
  'hero.ctaPrimary': 'डाउनलोड शुरू करें',
  'hero.ctaSecondary': 'विशेषताएँ देखें',
  'input.placeholder': 'सार्वजनिक Instagram लिंक पेस्ट करें — Reel, पोस्ट, IGTV या वीडियो…',
  'input.paste': 'पेस्ट',
  'input.process': 'प्रोसेस करें',
  'input.processing': 'प्रोसेस हो रहा है…',
  'input.dropHint': 'अपना लिंक बॉक्स में छोड़ें',
  'input.output.video': 'वीडियो · MP4',
  'input.output.audio': 'ऑडियो · MP3',
  'input.output.snapshots': 'स्नैपशॉट',
  'input.quality': 'क्वालिटी',
  'input.interval': 'अंतराल',
  'input.format': 'फ़ॉर्मैट',
  'input.custom': 'कस्टम',
  'input.seconds': 'सेकंड',
  'status.queued': 'कतार में',
  'status.processing': 'प्रोसेसिंग',
  'status.completed': 'पूर्ण',
  'status.failed': 'विफल',
  'status.expired': 'समाप्त',
  'dash.overview': 'अवलोकन',
  'dash.queue': 'प्रोसेसिंग कतार',
  'dash.recent': 'हाल की डाउनलोड',
  'dash.history': 'डाउनलोड इतिहास',
  'dash.files': 'सहेजी फ़ाइलें',
  'dash.settings': 'सेटिंग्स',
  'dash.admin': 'एडमिन एनालिटिक्स',
  'dash.emptyQueue': 'अभी कुछ प्रोसेस नहीं हो रहा। शुरू करने के लिए ऊपर URL पेस्ट करें।',
  'dash.emptyHistory': 'आपकी डाउनलोड यहाँ दिखेंगी।',
  'stats.today': 'आज की डाउनलोड',
  'stats.total': 'कुल डाउनलोड',
  'stats.storage': 'उपयोग किया स्टोरेज',
  'stats.topPlatform': 'सबसे प्रयुक्त प्लेटफ़ॉर्म',
  'common.download': 'डाउनलोड',
  'common.copy': 'लिंक कॉपी करें',
  'common.copied': 'कॉपी हो गया!',
  'common.share': 'साझा करें',
  'common.delete': 'हटाएँ',
  'common.loading': 'लोड हो रहा है…',
  'common.viewAll': 'सभी देखें',
  'common.language': 'भाषा',
  'common.notifications': 'सूचनाएँ',
  'common.expiresIn': 'समाप्ति',
  'footer.disclaimer':
    'केवल सार्वजनिक कंटेंट प्रोसेस होता है। कॉपीराइट कानूनों और प्रत्येक प्लेटफ़ॉर्म की सेवा शर्तों का पालन करना आपकी ज़िम्मेदारी है।',
  'processing.title': 'आपका मीडिया तैयार हो रहा है…',
  'processing.subtitle': 'कृपया प्रतीक्षा करें — क्लाउड फ़ंक्शंस आपकी फ़ाइल तैयार कर रहे हैं।',
  'processing.eta': 'अनुमानित प्रतीक्षा',
};

export const DICTIONARIES: Record<Language, Record<TranslationKey, string>> = { en, es, hi };
